import { z } from 'zod';

import {
  deleteProviderJson,
  getProviderJson,
  postProviderJson,
  putProviderJson
} from '../providerClient';

export const APPLICATION_QUESTION_TYPES = [
  'short_text',
  'long_text',
  'single_select',
  'multi_select',
  'boolean',
  'number',
  'date',
  'email',
  'phone'
] as const;

export type ApplicationQuestionType =
  (typeof APPLICATION_QUESTION_TYPES)[number];

export const APPLICATION_QUESTION_TYPE_LABELS: Record<
  ApplicationQuestionType,
  string
> = {
  short_text: 'Short answer',
  long_text: 'Paragraph',
  single_select: 'Choose one',
  multi_select: 'Choose any',
  boolean: 'Yes / no',
  number: 'Number',
  date: 'Date',
  email: 'Email',
  phone: 'Phone'
};

export const CHOICE_QUESTION_TYPES: ApplicationQuestionType[] = [
  'single_select',
  'multi_select'
];

const questionSchema = z.object({
  id: z.number(),
  sortOrder: z.number(),
  prompt: z.string(),
  helpText: z.string().nullable(),
  questionType: z.enum(APPLICATION_QUESTION_TYPES),
  options: z.array(z.string()).nullable(),
  isRequired: z.boolean()
});

export const applicationSchema = z.object({
  id: z.number(),
  title: z.string(),
  introText: z.string().nullable(),
  imageUrl: z.string().nullable(),
  providerImageUrl: z.string().nullable(),
  isDefault: z.boolean(),
  source: z.enum(['scraped', 'starter_template', 'manual']),
  sourceUrl: z.string().nullable(),
  attachToWaitlists: z.boolean(),
  attachToListings: z.boolean(),
  attachToVisits: z.boolean(),
  version: z.number(),
  questionCount: z.number(),
  questions: z.array(questionSchema),
  submissionCounts: z.object({
    open: z.number(),
    submitted: z.number(),
    pendingReview: z.number()
  }),
  createdAt: z.string(),
  updatedAt: z.string()
});

export type ProviderApplication = z.infer<typeof applicationSchema>;
export type ApplicationQuestion = z.infer<typeof questionSchema>;

const applicationListSchema = z.object({ items: z.array(applicationSchema) });

const submissionQuestionSchema = z.object({
  questionId: z.number().nullable(),
  sortOrder: z.number(),
  prompt: z.string(),
  helpText: z.string().nullable(),
  questionType: z.enum(APPLICATION_QUESTION_TYPES),
  options: z.array(z.string()).nullable(),
  isRequired: z.boolean()
});

const submissionAnswerSchema = z.object({
  questionIndex: z.number(),
  questionPrompt: z.string(),
  questionType: z.enum(APPLICATION_QUESTION_TYPES),
  answerText: z.string().nullable(),
  answerJson: z.unknown()
});

const submitterSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  photoUrl: z.string().nullable()
});

export type SubmissionSubmitter = z.infer<typeof submitterSchema>;

export const submissionSchema = z.object({
  id: z.number(),
  applicationId: z.number(),
  applicationTitle: z.string(),
  introText: z.string().nullable(),
  imageUrl: z.string().nullable(),
  providerId: z.number(),
  providerName: z.string().nullable().optional(),
  accountId: z.number().nullable(),
  submitter: submitterSchema.nullable(),
  conversationId: z.number().nullable(),
  sentMessageId: z.number().nullable(),
  status: z.enum(['sent', 'in_progress', 'submitted', 'withdrawn', 'expired']),
  reviewStatus: z.enum(['pending', 'done']),
  reviewNote: z.string().nullable(),
  questions: z.array(submissionQuestionSchema),
  answers: z.array(submissionAnswerSchema),
  sentAt: z.string(),
  startedAt: z.string().nullable(),
  submittedAt: z.string().nullable(),
  reviewedAt: z.string().nullable()
});

export type ApplicationSubmission = z.infer<typeof submissionSchema>;
export type SubmissionQuestion = z.infer<typeof submissionQuestionSchema>;
export type SubmissionAnswer = z.infer<typeof submissionAnswerSchema>;

const submissionListSchema = z.object({ items: z.array(submissionSchema) });

export type ApplicationQuestionInput = {
  id?: number | null;
  prompt: string;
  helpText?: string | null;
  questionType: ApplicationQuestionType;
  options?: string[] | null;
  isRequired?: boolean;
};

export type ApplicationInput = {
  title: string;
  introText?: string | null;
  imageUrl?: string | null;
  isDefault?: boolean;
  attachToWaitlists?: boolean;
  attachToListings?: boolean;
  attachToVisits?: boolean;
  questions: ApplicationQuestionInput[];
};

export const getApplications = () =>
  getProviderJson('/providers/applications', { schema: applicationListSchema });

export const getApplication = (id: number) =>
  getProviderJson(`/providers/applications/${id}`, {
    schema: applicationSchema
  });

export const createApplication = (input: ApplicationInput) =>
  postProviderJson('/providers/applications', input, {
    schema: applicationSchema
  });

export const seedApplication = (preset: 'starter' | 'blank') =>
  postProviderJson(
    '/providers/applications/seed',
    { preset },
    { schema: applicationSchema }
  );

export const updateApplication = (
  id: number,
  input: Partial<ApplicationInput>
) =>
  putProviderJson(`/providers/applications/${id}`, input, {
    schema: applicationSchema
  });

export const deleteApplication = (id: number) =>
  deleteProviderJson(`/providers/applications/${id}`);

export const sendApplication = (
  id: number,
  input: { conversationId: number; message?: string }
) =>
  postProviderJson(`/providers/applications/${id}/send`, input, {
    schema: z.object({
      submissionId: z.number(),
      title: z.string(),
      imageUrl: z.string().nullable(),
      url: z.string()
    })
  });

export const getApplicationSubmissions = (filters?: {
  applicationId?: number;
  conversationId?: number;
  reviewStatus?: string;
}) =>
  getProviderJson('/providers/applications/submissions', {
    query: filters,
    schema: submissionListSchema
  });

export const reviewApplicationSubmission = (
  id: number,
  input: {
    reviewStatus: 'pending' | 'done';
    reviewNote?: string | null;
  }
) =>
  postProviderJson(`/providers/applications/submissions/${id}/review`, input, {
    schema: submissionSchema
  });

export const syncRequestSchema = z.object({
  id: z.number(),
  websiteUrl: z.string(),
  note: z.string().nullable(),
  status: z.enum(['pending', 'completed', 'dismissed']),
  createdAt: z.string(),
  handledAt: z.string().nullable(),
  handledNote: z.string().nullable()
});

export type ApplicationSyncRequest = z.infer<typeof syncRequestSchema>;

export const getApplicationSyncRequest = () =>
  getProviderJson('/providers/applications/sync-request', {
    schema: z.object({ request: syncRequestSchema.nullable() })
  });

export const requestApplicationSync = (input: {
  websiteUrl: string;
  note?: string | null;
}) =>
  postProviderJson('/providers/applications/sync-request', input, {
    schema: z.object({ request: syncRequestSchema })
  });
